import { PageBottomPortal } from "@/components/ui/page-bottom-portal"
import { dedupeImageCredits, imageLicenseUrl, type ImageCredit } from "@/lib/image-credits"

/**
 * The page's photo credits as one muted line. By default it portals below the
 * site footer; `placement="inline"` renders it in place, which blog posts use to
 * put credits at the end of the article. Credits never render next to the hero
 * or figures themselves (each credited image also carries its credit as a title
 * tooltip). Renders nothing when the page has no credited images.
 */
export function ImageCreditsFooter({
  credits,
  placement = "page-bottom",
}: {
  credits: Array<ImageCredit | null | undefined>
  placement?: "page-bottom" | "inline"
}) {
  const list = dedupeImageCredits(credits)
  if (list.length === 0) return null

  const line = (
    <p data-image-credits className="m-0 text-[11px] leading-relaxed text-muted-foreground">
          Photos:{" "}
          {list.map((credit, i) => (
            <span key={`${credit.name}|${credit.source ?? ""}`}>
              {i > 0 && ", "}
              {credit.url ? (
                <a
                  href={credit.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  {credit.name}
                </a>
              ) : (
                credit.name
              )}
              {credit.source ? ` / ${credit.source}` : ""}
              {credit.license && (
                <>
                  {" · "}
                  {imageLicenseUrl(credit.license) ? (
                    <a href={imageLicenseUrl(credit.license)} target="_blank" rel="noopener noreferrer"
                      className="underline underline-offset-2 hover:text-foreground">
                      {credit.license}
                    </a>
                  ) : credit.license}
                </>
              )}
            </span>
          ))}
        </p>
  )

  if (placement === "inline") {
    return <div className="image-credits-inline mt-6 border-t border-gray-200/70 pt-3">{line}</div>
  }

  return (
    <PageBottomPortal>
      <div className="image-credits-footer mx-auto max-w-7xl px-4 pb-3 pt-3 md:px-6 lg:px-8">{line}</div>
    </PageBottomPortal>
  )
}
